import api from './api';
import { Page } from './warehouse.service';

export interface RoutingOperation {
  id?: string;
  sequence: number;
  name: string;
  workCenterId: string | null;
  workCenterName?: string | null;
  workCenterCode?: string | null;
  setupMinutes: number;
  runMinutesPerUnit: number;
  isOutsourced: boolean;
  description: string | null;
  // Rate plus overhead of the work center, per hour
  hourlyCost?: number;
}

export interface Routing {
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  operations: RoutingOperation[];
}

export interface RoutingRow {
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  operations: number;
  outsourced: number;
  setupMinutes: number;
  runMinutesPerUnit: number;
  runCostPerUnit: number;
  steps: string;
  updatedAt: string;
}

export interface RoutingSummary {
  productsWithRouting: number;
  operations: number;
  operationsWithoutWorkCenter: number;
  activeProducts: number;
}

export type RoutingOperationInput = Pick<
  RoutingOperation,
  'sequence' | 'name' | 'workCenterId' | 'setupMinutes' | 'runMinutesPerUnit' | 'isOutsourced' | 'description'
>;

export type OperationStatus = 'pending' | 'in_progress' | 'completed';

export interface WorkOrderOperation {
  id: string;
  workOrderId: number;
  workOrderNumber: string;
  workOrderStatus: string;
  plannedQuantity: number;
  plannedEndDate: string;
  productName: string;
  sku: string;
  unit: string;
  sequence: number;
  name: string;
  workCenterId: string | null;
  workCenterName: string | null;
  isOutsourced: boolean;
  status: OperationStatus;
  plannedMinutes: number;
  actualMinutes: number;
  goodQuantity: number;
  rejectedQuantity: number;
  // Sent to rework in all, and how many of those are still waiting to be booked as good or rejected
  reworkQuantity: number;
  inReworkQuantity: number;
  reworkMinutes: number;
  // What the step can work on, and what is still to be booked
  inputQuantity: number;
  remainingQuantity: number;
  plannedCost: number;
  actualCost: number;
}

export interface JobCard {
  id: string;
  workOrderOperationId: string;
  sequence: number;
  operationName: string;
  workDate: string;
  operatorName: string | null;
  minutes: number;
  goodQuantity: number;
  rejectedQuantity: number;
  reworkQuantity: number;
  rejectionReason: string | null;
  scrapReasonName: string | null;
  // Books the outcome of pieces that were in rework
  fromRework: boolean;
  notes: string | null;
  createdAt: string;
}

export interface WorkOrderOperations {
  workOrderId: number;
  workOrderNumber: string;
  status: string;
  productName: string;
  sku: string;
  unit: string;
  plannedQuantity: number;
  operations: WorkOrderOperation[];
  jobCards: JobCard[];
  plannedMinutes: number;
  actualMinutes: number;
  plannedCost: number;
  actualCost: number;
}

export interface JobCardInput {
  workDate?: string;
  operatorName?: string | null;
  minutes: number;
  goodQuantity: number;
  rejectedQuantity?: number;
  reworkQuantity?: number;
  rejectionReason?: string | null;
  scrapReasonId?: string | null;
  fromRework?: boolean;
  notes?: string | null;
}

export interface OperationQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: OperationStatus | '';
  workCenterId?: string;
  workOrderId?: number;
  openOnly?: boolean;
}

export interface OperationSummary {
  openOperations: number;
  runningOperations: number;
  workOrdersInProgress: number;
  rejectedUnits: number;
  hoursBookedToday: number;
}

// Empty filters are left out of the request
const clean = (params: object) =>
  Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

export const routingService = {
  getPage: async (params: { page?: number; limit?: number; search?: string }): Promise<Page<RoutingRow>> =>
    (await api.get('/manufacturing/routings', { params: clean(params) })).data,
  getSummary: async (): Promise<RoutingSummary> => (await api.get('/manufacturing/routings/summary')).data,
  getOne: async (productId: string): Promise<Routing> => (await api.get(`/manufacturing/routings/${productId}`)).data,
  save: async (productId: string, operations: RoutingOperationInput[]): Promise<Routing> =>
    (await api.put(`/manufacturing/routings/${productId}`, { operations })).data,

  getOperations: async (params: OperationQueryParams): Promise<Page<WorkOrderOperation>> =>
    (await api.get('/manufacturing/job-cards', { params: clean(params) })).data,
  getOperationSummary: async (): Promise<OperationSummary> => (await api.get('/manufacturing/job-cards/summary')).data,
  getWorkOrderOperations: async (workOrderId: number): Promise<WorkOrderOperations> =>
    (await api.get(`/manufacturing/work-orders/${workOrderId}/operations`)).data,
  loadForWorkOrder: async (workOrderId: number): Promise<WorkOrderOperations> =>
    (await api.post(`/manufacturing/work-orders/${workOrderId}/operations/load`)).data,
  book: async (workOrderId: number, operationId: string, data: JobCardInput): Promise<WorkOrderOperations> =>
    (await api.post(`/manufacturing/work-orders/${workOrderId}/operations/${operationId}/job-cards`, data)).data,
  removeJobCard: async (workOrderId: number, jobCardId: string): Promise<WorkOrderOperations> =>
    (await api.delete(`/manufacturing/work-orders/${workOrderId}/job-cards/${jobCardId}`)).data,
};
